package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.ComplianceWarningDto

private val complianceScope = MainScope()

@Composable
fun CompliancePage(workplaceId: Long) {
    var warnings by remember { mutableStateOf<List<ComplianceWarningDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var checking by remember { mutableStateOf(false) }
    var feedbackMsg by remember { mutableStateOf<String?>(null) }

    fun loadWarnings() {
        loading = true
        complianceScope.launch {
            complianceRepo.getWarnings(workplaceId).onSuccess { warnings = it }
            loading = false
        }
    }

    LaunchedEffect(workplaceId) { loadWarnings() }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {

        feedbackMsg?.let { msg ->
            Div({
                style {
                    property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)")
                    property("padding", "0.75rem 1.25rem"); property("border-radius", "14px"); property("font-weight", "600")
                }
            }) { Text(msg) }
        }

        // Header
        Div({
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.25rem")
                property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center")
            }
        }) {
            Div {
                H3({ style { property("margin", "0 0 0.25rem 0"); property("font-size", "1.3rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                    Text("Compliance")
                }
                P({ style { property("margin", "0"); property("color", "var(--rp-muted)"); property("font-size", "0.85rem") } }) {
                    Text("Arbetstidsdirektiv & viloregler")
                }
            }
            Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("align-items", "center") } }) {
                val active = warnings.count { !it.acknowledged }
                if (active > 0) {
                    Span({ style { property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)"); property("padding", "0.4rem 1rem"); property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.85rem") } }) {
                        Text("$active aktiva")
                    }
                }
                Button({
                    onClick {
                        checking = true
                        complianceScope.launch {
                            complianceRepo.runCheck(workplaceId).onSuccess { newWarnings ->
                                feedbackMsg = "\u2713 Kontroll klar \u2014 ${newWarnings.size} nya varningar"
                                loadWarnings()
                            }
                            checking = false
                        }
                    }
                    if (checking) disabled()
                    style {
                        property("padding", "0.7rem 1.5rem"); property("border", "none")
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color", "white")
                        property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.9rem"); property("cursor", "pointer")
                    }
                }) { Text(if (checking) "Kontrollerar\u2026" else "K\u00F6r kontroll") }
            }
        }

        // Warnings list
        Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem") } }) {
            if (loading) {
                Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-subtle)") } }) { Text("Laddar\u2026") }
            } else if (warnings.isEmpty()) {
                Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-muted)") } }) {
                    Div({ style { property("font-size", "2rem"); property("margin-bottom", "0.5rem") } }) { Text("\u2705") }
                    Text("Inga varningar! Allt ser bra ut.")
                }
            } else {
                warnings.forEach { warning ->
                    val (iconBg, iconColor, icon) = when (warning.severity) {
                        "critical" -> Triple("var(--rp-err-bg)", "var(--rp-err-text)", "\u26A0")
                        "warning" -> Triple("var(--rp-warn-bg)", "var(--rp-warn-text)", "\u26A0")
                        else -> Triple("var(--rp-info-bg)", "var(--rp-pine)", "\u2139")
                    }

                    Div({
                        style {
                            property("display", "flex"); property("gap", "1rem"); property("padding", "1rem")
                            property("border-bottom", "1px solid var(--rp-hl-med)"); property("align-items", "flex-start")
                            if (warning.acknowledged) property("opacity", "0.5")
                        }
                    }) {
                        Div({
                            style {
                                property("width", "40px"); property("height", "40px"); property("border-radius", "12px")
                                property("background", iconBg); property("display", "flex")
                                property("align-items", "center"); property("justify-content", "center")
                                property("font-size", "1.1rem"); property("flex-shrink", "0")
                            }
                        }) { Text(icon) }

                        Div({ style { property("flex", "1") } }) {
                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("margin-bottom", "0.25rem") } }) {
                                Span({ style { property("font-weight", "700"); property("font-size", "0.9rem"); property("color", "var(--rp-text)") } }) { Text(warning.ruleLabel) }
                                Span({
                                    style {
                                        property("background", iconBg); property("color", iconColor)
                                        property("padding", "0.15rem 0.5rem"); property("border-radius", "50px")
                                        property("font-size", "0.65rem"); property("font-weight", "700"); property("text-transform", "uppercase")
                                    }
                                }) { Text(warning.severity) }
                                if (warning.acknowledged) {
                                    Span({ style { property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)"); property("padding", "0.15rem 0.5rem"); property("border-radius", "50px"); property("font-size", "0.65rem"); property("font-weight", "700") } }) {
                                        Text("Kvitterad")
                                    }
                                }
                            }
                            P({ style { property("margin", "0 0 0.25rem 0"); property("color", "var(--rp-text)"); property("font-size", "0.85rem") } }) { Text(warning.message) }
                            Span({ style { property("color", "var(--rp-muted)"); property("font-size", "0.75rem") } }) { Text("${warning.userName} \u2022 ${warning.referenceDate}") }
                        }

                        if (!warning.acknowledged) {
                            Button({
                                onClick {
                                    complianceScope.launch {
                                        complianceRepo.acknowledge(warning.id).onSuccess { loadWarnings() }
                                    }
                                }
                                style {
                                    property("padding", "0.4rem 0.8rem"); property("border", "none")
                                    property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)")
                                    property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.75rem"); property("cursor", "pointer")
                                    property("white-space", "nowrap")
                                }
                            }) { Text("Kvittera") }
                        }
                    }
                }
            }
        }
    }
}
